import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable, tap } from 'rxjs';
import { Team, TeamUsers, User, UserPermissions } from '../types/user';
import { StateService } from '@modules/settings/services/state.service';
import { UniqueID } from '@modules/elements/types/unique-id';
import { Account } from '../types/account';
import { groupBy } from 'lodash-es';
import { NetworkService } from '@modules/core/services/network.service';
import { warmUpObservable } from '@decorators';
import { ApiClient } from '../types/api-client';

@Injectable({
  providedIn: 'root'
})
export class UsersService {

  // Private
  private users = new BehaviorSubject<User[]>([]);
  private teams = new BehaviorSubject<Team[]>([]);

  /**
   * Constructor
   */

  constructor(
    private stateService: StateService,
    private networkService: NetworkService,
  ) {
    // Request Users
    this.requestUsers();
    this.requestTeams();
  }

  /**
   * Account
   */

  getAccount(): Observable<Account | undefined> {
    return this.stateService.getState()
      .pipe(
        map(state => state.account),
        tap(account => {
          const user = this.users.value.find(user => user.email === account?.email);
          if (user && account && ((!account?.name && user.name) || (!account?.photoUrl && user?.photoUrl))) {
            account.name = user.name;
            account.photoUrl = user.photoUrl;
            this.updateAccount(account);
          }
        })
      );
  }

  updateAccount(account: Account): void {
    const user = this.users.value.find(user => user.email === account.email);
    if (user) {
      user.name = account.name;
      user.photoUrl = account.photoUrl;
    }
    this.stateService.updateState({ account });
  }
  
  getAccountTeam(): Observable<Team | undefined> {
    return combineLatest([
      this.getAccount(),
      this.teams.asObservable()
    ])
      .pipe(
        map(([account, teams]) => teams.find(team => team.id === account?.team?.id))
      )
  }

  getPermissions(): Observable<UserPermissions[] | undefined> {
    return this.getAccountTeam()
      .pipe(
        map(team => team?.permissions)
        // map(team => [])
      )
  }

  hasAdminAccess(): Observable<boolean> {
    return this.getAccountTeam()
      .pipe(
        map(team => !!team?.permissions?.includes('admin:all'))
      )
  }

  /**
   * Users
   */

  requestUsers(): void {
    this.networkService.getUsers().subscribe(users => this.users.next(users));
  }

  getUsers(): Observable<User[]> {
    return this.users.asObservable();
  }

  updateUser(user: User): void {
    this.networkService.updateUser(user).subscribe(() => this.requestUsers());
  }

  @warmUpObservable
  inviteUser(email: string, role: string, teamId: string): Observable<any> {
    return this.networkService.invite(email, role, teamId)
      .pipe(
        tap(user => this.requestUsers())
      );
  }

  deleteUser(id: string): void {
    this.networkService.deleteUser(id).subscribe(() => this.requestUsers());
  }

  /**
   * Teams
   */

  requestTeams(): void {
    this.networkService.getTeams().subscribe(teams => this.teams.next(teams));
  }

  getTeams(): Observable<Team[]> {
    return this.teams.asObservable();
  }

  @warmUpObservable
  createTeam(team: Team): Observable<any> {
    return this.networkService.createTeam(team.name!, team.permissions ?? []).pipe(
      tap(() => {
        this.requestTeams();
        this.requestUsers();
      })
    );
  }

  @warmUpObservable
  updateTeam(team: Team): Observable<any> {
    return this.networkService.updateTeam(team).pipe(
      tap(() => {
        this.requestTeams();
        this.requestUsers();
      })
    );
  }

  @warmUpObservable
  deleteTeam(id: string): Observable<any> {
    return this.networkService.deleteTeam(id).pipe(
      tap(() => {
        this.requestTeams();
        this.requestUsers();
      })
    );
  }

  getTeamUsers(): Observable<TeamUsers[]> {
    return combineLatest([
      this.users.asObservable(),
      this.getAccountTeam()
    ])
      .pipe(
        map(([users, team]) => {
          const teamUsers: TeamUsers[] = [];
          const availableUsers = users.filter(user => team?.permissions?.includes('admin:all') || user.team?.id === team?.id);
          const groupUsers = groupBy(availableUsers, user => user.team?.id);
          Object.keys(groupUsers).forEach(key => {teamUsers.push({team: groupUsers[key][0].team, users: groupUsers[key]})})
          return teamUsers;
        })
      )
  }

  /**
   * API Keys
   */
  
  getApiKeys(): Observable<ApiClient[]> {
    return this.networkService.getApiKeys();
  }

  getApiKey(id: string): Observable<ApiClient> {
    return this.networkService.getApiKey(id);
  }

  @warmUpObservable
  createApiKey(key: ApiClient): Observable<ApiClient> {
    return this.networkService.createApiKey(key);
  }

  @warmUpObservable
  updateApiKey(key: ApiClient): Observable<ApiClient> {
    return this.networkService.updateApiKey(key);
  }

  @warmUpObservable
  deleteApiKey(id: string): Observable<any> {
    return this.networkService.deleteApiKey(id);
  }

  revokeApiKey(id: string): Observable<any> {
    return this.networkService.revokeApiKey(id);
  }

  @warmUpObservable
  rotateApiKey(id: string): Observable<any> {
    return this.networkService.rotateApiKey(id);
  }

}
